import { motion, AnimatePresence } from "framer-motion";
import { Zap, Heart, Star, X, Crown, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { tx } from "../lib/tx";

export default function SuperLikeModal({ isOpen, onClose, onConfirm, targetUser, userCredits = 0 }) {
  const SUPER_LIKE_COST = 20;
  const hasEnoughCredits = userCredits >= SUPER_LIKE_COST;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
            onClick={onClose} 
          />
          
          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-sm bg-background rounded-3xl p-6 overflow-hidden"
          >
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-red-500/20" />
            
            {/* Content */}
            <div className="relative">
              {/* Close button */}
              <button 
                onClick={onClose}
                className="absolute -top-1 -right-1 p-2 rounded-full hover:bg-white/10"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                  <Zap className="w-10 h-10 text-white" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Super Like 💫
              </h2>
              
              {targetUser && (
                <p className="text-center text-muted-foreground mb-4">
                  {tx("Envoyer un Super Like à")} <span className="text-white font-semibold">{targetUser.name}</span>
                </p>
              )}

              {/* Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-pink-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{tx("Coup de cœur instantané")}</p>
                    <p className="text-xs text-muted-foreground">{tx("Le membre sera notifié immédiatement")}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{tx("Profil mis en avant")}</p>
                    <p className="text-xs text-muted-foreground">{tx("Votre profil sera prioritaire")}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{tx("Messages prioritaires")}</p>
                    <p className="text-xs text-muted-foreground">{tx("Vos messages seront vus en premier")}</p>
                  </div>
                </div>
              </div>

              {/* Cost */}
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="text-amber-400 font-bold">{SUPER_LIKE_COST} {tx("crédits")}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {tx("Vos crédits :")} <span className={hasEnoughCredits ? "text-green-400" : "text-red-400"}>{userCredits}</span>
                </p>
              </div>

              {/* Actions */}
              {hasEnoughCredits ? (
                <Button
                  onClick={() => onConfirm(SUPER_LIKE_COST)}
                  className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 rounded-xl text-lg font-bold"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  {tx("Envoyer le Super Like")}
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-center text-red-400 text-sm">
                    {tx("Crédits insuffisants")}
                  </p>
                  <Button
                    onClick={() => {/* Navigate to buy credits */}}
                    className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl"
                  >
                    {tx("Acheter des crédits")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="w-full h-12 rounded-xl border-white/20"
                  >
                    {tx("Annuler")}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
